import { useState } from "react";
import { api, ApiError } from "@/lib/api";
import type { GuestProfile } from "@/lib/guestAuth";
import {
  GUEST_ADDRESS_LABELS,
  guestSummaryLines,
  type GuestAddressField,
} from "@/lib/guestProfile";
import type { GuestProfileUpdate } from "@/lib/guestPortalTypes";

type Props = {
  profile: GuestProfile;
  onSaved: (profile: GuestProfile) => void;
};

const ADDRESS_FIELDS: GuestAddressField[] = ["street", "postal_code", "city"];

export function GuestProfilePanel({ profile, onSaved }: Props) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<GuestProfileUpdate>({
    first_name: profile.first_name,
    last_name: profile.last_name,
    phone: profile.phone ?? "",
    street: profile.street ?? "",
    postal_code: profile.postal_code ?? "",
    city: profile.city ?? "",
    country_code: profile.country_code || "DE",
  });

  function startEdit() {
    setForm({
      first_name: profile.first_name,
      last_name: profile.last_name,
      phone: profile.phone ?? "",
      street: profile.street ?? "",
      postal_code: profile.postal_code ?? "",
      city: profile.city ?? "",
      country_code: profile.country_code || "DE",
    });
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError(null);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError("Vor- und Nachname sind Pflichtfelder.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await api.patch<GuestProfile>("/api/v1/public/guest/me", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim() || null,
        street: form.street.trim() || null,
        postal_code: form.postal_code.trim() || null,
        city: form.city.trim() || null,
        country_code: form.country_code.trim().toUpperCase() || "DE",
      });
      onSaved(updated);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  const summary = guestSummaryLines({
    firstName: profile.first_name,
    lastName: profile.last_name,
    email: profile.email ?? "",
    phone: profile.phone ?? "",
    street: profile.street ?? "",
    postalCode: profile.postal_code ?? "",
    city: profile.city ?? "",
    licensePlate: profile.license_plate ?? "",
  });

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Meine Daten</h2>
        {!editing ? (
          <button type="button" className="bk-btn-secondary px-4 py-2 text-sm" onClick={startEdit}>
            Daten korrigieren
          </button>
        ) : null}
      </div>

      {!editing ? (
        <dl className="divide-y divide-slate-100 rounded-lg border border-slate-100 bg-slate-50/50">
          {summary.map((line) => (
            <div key={line.label} className="flex flex-col gap-0.5 px-4 py-3 sm:flex-row sm:gap-4">
              <dt className="shrink-0 text-sm font-medium text-slate-500 sm:w-28">{line.label}</dt>
              <dd className="text-sm text-slate-900">{line.value}</dd>
            </div>
          ))}
          <div className="flex flex-col gap-0.5 px-4 py-3 sm:flex-row sm:gap-4">
            <dt className="shrink-0 text-sm font-medium text-slate-500 sm:w-28">Land</dt>
            <dd className="text-sm text-slate-900">{profile.country_code}</dd>
          </div>
        </dl>
      ) : (
        <form onSubmit={(e) => void handleSave(e)} className="space-y-4 rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-slate-600">
            Bitte prüfen Sie Ihre Angaben. Die E-Mail-Adresse kann nur über die Rezeption geändert
            werden.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Vorname</span>
              <input
                className="bk-input"
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                required
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Nachname</span>
              <input
                className="bk-input"
                value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                required
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">E-Mail</span>
            <input className="bk-input bg-slate-50" value={profile.email ?? ""} disabled />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Telefon</span>
            <input
              className="bk-input"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </label>
          {ADDRESS_FIELDS.map((key) => (
            <label key={key} className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">
                {GUEST_ADDRESS_LABELS[key]}
              </span>
              <input
                className="bk-input"
                value={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              />
            </label>
          ))}
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Land (ISO)</span>
            <input
              className="bk-input max-w-[6rem] uppercase"
              maxLength={2}
              value={form.country_code}
              onChange={(e) =>
                setForm((f) => ({ ...f, country_code: e.target.value.toUpperCase() }))
              }
            />
          </label>
          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </p>
          ) : null}
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="submit" className="bk-btn-primary flex-1" disabled={saving}>
              {saving ? "Wird gespeichert …" : "Änderungen speichern"}
            </button>
            <button
              type="button"
              className="bk-btn-secondary flex-1"
              onClick={cancelEdit}
              disabled={saving}
            >
              Abbrechen
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
